import React, { useState, useEffect } from 'react';
import { FileText, Calculator, Mail, CheckCircle } from 'lucide-react';

const steps = [
  {
    icon: FileText,
    title: "Daten eingeben",
    description: "Geben Sie grundlegende Informationen zu Ihrem Unternehmen ein."
  },
  {
    icon: Calculator,
    title: "KI-Analyse",
    description: "Unsere KI berechnet den Wert basierend auf bewährten Methoden."
  },
  {
    icon: Mail,
    title: "Ergebnis erhalten",
    description: "Erhalten Sie Ihre detaillierte Bewertung per E-Mail."
  },
  {
    icon: CheckCircle,
    title: "Beratung (optional)",
    description: "Lassen Sie sich von unseren Experten beraten."
  }
];

// Parallax hook
const useParallax = () => {
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return scrollY;
};

const ProcessSection = () => {
  const scrollY = useParallax();
  
  return (
    <section className="py-24 bg-[#f4f5f3] relative overflow-hidden">
      {/* Parallax Decorative Elements - MORE VISIBLE */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Circles */}
        <div 
          className="absolute w-44 h-44 rounded-full border-2 border-[#FF0000]/25 hidden lg:block"
          style={{ 
            top: '10%', 
            left: '3%',
            transform: `translateY(${(scrollY - 1200) * 0.18}px)` 
          }}
        />
        <div 
          className="absolute w-36 h-36 rounded-full bg-[#FF0000]/10 hidden lg:block"
          style={{ 
            bottom: '15%', 
            right: '5%',
            transform: `translateY(${(scrollY - 1200) * -0.2}px)` 
          }}
        />
        <div 
          className="absolute w-24 h-24 rounded-full bg-[#FFE5E5]/60 hidden lg:block"
          style={{ 
            top: '50%', 
            right: '2%',
            transform: `translateY(${(scrollY - 1200) * 0.1}px)` 
          }}
        />
        
        {/* Arrows */}
        <img 
          src="/arrow-down-right.webp" 
          alt="" 
          className="absolute w-32 h-auto opacity-25 hidden lg:block"
          style={{ 
            top: '5%', 
            right: '8%',
            transform: `translateY(${(scrollY - 1200) * 0.22}px) rotate(${(scrollY - 1200) * 0.02}deg)` 
          }}
          aria-hidden="true"
        />
        <img 
          src="/arrow-right.webp" 
          alt="" 
          className="absolute w-28 h-auto opacity-20 hidden xl:block"
          style={{ 
            bottom: '20%', 
            left: '2%',
            transform: `translateY(${(scrollY - 1200) * -0.15}px)` 
          }}
          aria-hidden="true"
        />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="caption mb-4 block">So funktioniert&apos;s</span>
          <h2 className="heading-2 mb-6">
            In <span className="text-[#FF0000]">4 einfachen Schritten</span> zur Bewertung
          </h2>
          <p className="body-medium max-w-2xl mx-auto">
            Unser Prozess ist transparent und einfach. Keine versteckten Kosten, keine Verpflichtungen.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div key={step.title} className="relative">
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-[calc(50%+40px)] w-[calc(100%-80px)] h-0.5 bg-gradient-to-r from-[#FF0000]/30 to-[#FF0000]/10" />
                )}
                
                <div className="text-center">
                  {/* Step Number & Icon */}
                  <div className="relative inline-flex mb-6">
                    <div className="w-24 h-24 rounded-2xl bg-white shadow-lg flex items-center justify-center border border-gray-100">
                      <IconComponent className="w-10 h-10 text-[#FF0000]" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#FF0000] text-white flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                  </div>
                  
                  <h3 className="heading-3 mb-3">{step.title}</h3>
                  <p className="body-medium">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
